import { createVgpsLivekitWebRuntime, StreamSocketTransport } from '@vgps/livekit_web';
import { HybridPlayerAdapter } from '@vgps/livekit_player';

// ── Runtime singleton state machine ──────────────────────────────────────────
// HMR + StrictMode + auth-token churn used to race ensureRuntime() against
// disposeRuntime(): the dispose was async, but the old `runtimePromise = null`
// was set only AFTER `await runtime.dispose()` resolved, so a concurrent
// ensureRuntime() could observe a still-non-null promise pointing at a
// runtime that was already in the process of tearing down.  When that
// runtime's WebSocket finally closed and the next ensureRuntime() created a
// fresh runtime with a new clientId, the signaling server briefly saw two
// distinct viewers for the same camera channel and emitted a spurious
// `session_conflict` to the original (now-dying) WS.
//
// State machine guarantees mutual exclusion:
//   idle        → no runtime; the next ensure() may construct one
//   connecting  → an ensure() is in flight; concurrent calls await its promise
//   ready       → runtime is alive; ensure() refreshes auth and returns it
//   disposing   → a dispose() is in flight; ensure() awaits it before re-entering idle
let state = { kind: 'idle' };

function getWasmBaseUrl() {
  const configured = process.env.REACT_APP_LIVEKIT_WASM_BASE_URL || '/static/wasm/';
  return configured.endsWith('/') ? configured : `${configured}/`;
}

// Player tunables wired from build-time env (CRA bakes REACT_APP_*). Undefined
// when unset → SDK falls back to its own defaults.
function envInt(name) {
  const v = process.env[name];
  if (v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}
function playerOpts() {
  return {
    jitterBufferInitialMs: envInt('REACT_APP_LIVEKIT_JITTER_INITIAL_MS'),
    jitterBufferMaxMs: envInt('REACT_APP_LIVEKIT_JITTER_MAX_MS'),
    recoveringFallbackMs: envInt('REACT_APP_LIVEKIT_RECOVERING_FALLBACK_MS'),
  };
}

function createRuntimeConfig() {
  const config = {
    signalingServer: {
      url: process.env.REACT_APP_LIVEKIT_SIGNALING_URL,
      audience: process.env.REACT_APP_LIVEKIT_SIGNALING_AUDIENCE || 'web-sdk',
      headers: {},
    },
    transportDefaults: {
      iceServers: [],
    },
    renderDefaults: {
      backendPreference: 'auto',
      mutedAutoplay: true,
      playsInline: true,
    },
    streamSocketTransportFactory: () =>
      new StreamSocketTransport(new HybridPlayerAdapter(
        getWasmBaseUrl(),
        playerOpts()
      )),
  };

  return config;
}

async function constructAndConnect(accessToken) {
  const rt = createVgpsLivekitWebRuntime(createRuntimeConfig());
  await rt.setAuthContext({ accessToken });
  if (rt.signalingConnectionState() !== 'connected') {
    await rt.connectSignaling();
  }
  return rt;
}

export async function ensureRuntime(accessToken) {
  // Wait out an in-flight dispose so we never construct on top of a tearing-down WS.
  if (state.kind === 'disposing') {
    try { await state.promise; } catch { /* ignore */ }
    state = { kind: 'idle' };
  }

  if (state.kind === 'ready') {
    await state.runtime.setAuthContext({ accessToken });
    if (state.runtime.signalingConnectionState() !== 'connected') {
      await state.runtime.connectSignaling();
    }
    return state.runtime;
  }

  if (state.kind === 'connecting') {
    return state.promise;
  }

  const promise = (async () => {
    try {
      const rt = await constructAndConnect(accessToken);
      state = { kind: 'ready', runtime: rt };
      return rt;
    } catch (err) {
      state = { kind: 'idle' };
      throw err;
    }
  })();
  state = { kind: 'connecting', promise };
  return promise;
}

export async function disposeRuntime() {
  if (state.kind === 'idle' || state.kind === 'disposing') return;
  // If a connect is still in flight, wait for it to settle so dispose() runs
  // against a fully-constructed runtime (otherwise we leak).
  let runtime;
  if (state.kind === 'connecting') {
    try { runtime = await state.promise; } catch { state = { kind: 'idle' }; return; }
  } else {
    runtime = state.runtime;
  }
  const promise = runtime.dispose();
  state = { kind: 'disposing', promise };
  try { await promise; } finally { state = { kind: 'idle' }; }
}
